import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiError, api } from '@/lib/api';
import {
  Button,
  Checkbox,
  ConfirmDialog,
  EmptyState,
  Field,
  Input,
  Modal,
  MultiCombobox,
  Skeleton,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { renderInclusion } from '@/lib/inclusions';
import { denialReason } from '@/lib/permissions';
import { useAuth } from '@/lib/use-auth';
import type {
  CampaignInclusion,
  ProductInclusionPresetBand,
  ProductInclusionPresetBandInput,
} from '@/lib/types';

/**
 * The QUANTITY-BAND ladder for one product — what a line should already have
 * ticked once it names this product at a quantity in range. Migration 173.
 *
 * ## This is NOT the offer dialog, and folding the two would destroy data
 *
 * `ProductInclusionsModal` (catalog-admin) writes what a product **OFFERS** —
 * keyed by region, the set a line MAY pick from. This writes what a line
 * **STARTS WITH** — keyed by quantity band, carrying no region at all. Two
 * resources, two `PUT`s, and both are whole-set replaces: an administrator
 * editing a band here must never have to re-send the region-split offer rows,
 * because a forgotten key CLEARS and the smaller edit would then perform the
 * larger destruction.
 *
 * ## The four rules, all owner-decided and none of them re-litigable here
 *
 * ⚠️ **`maxQuantity: null` is OPEN-ENDED, never zero.** A `?? 0` makes the top
 * band cover nothing, which reads as "no preset configured" on exactly the
 * largest orders — the one place somebody is most likely to be looking. The
 * draft spells it as a separate {@link BandDraft.openEnded} flag rather than as
 * an empty string, so "no upper limit" and "I have not typed the upper limit
 * yet" cannot share one falsy value.
 *
 * ⚠️ **A GAP PRESETS NOTHING, and that is allowed.** A 1-4 / 10+ ladder answers
 * `band: null` for quantity 7 rather than falling back to the nearest band.
 * **Nothing here auto-fills a gap** — not on load, not on save, not when a band
 * is removed. Adding a band seeds its start from the previous band's end purely
 * as a default a person can overwrite; it is a convenience on ONE new row, never
 * a repair of the ladder.
 *
 * ⚠️ **OVERLAPS ARE THE SERVER'S REFUSAL, AND THE LOCAL CHECK NEVER BLOCKS A
 * SAVE.** {@link overlappingBands} exists to mark the offending entry before a
 * round trip, and that is all it does: Save stays enabled, the request goes, and
 * the server's own 422 is what is rendered. A local rule that disabled the
 * button would become the only guard in practice, and the first time it
 * disagreed with the server — over an open-ended band, an equal boundary, a rule
 * that moves in the other repo — it would refuse a legal ladder with a false
 * statement and nobody could get past it. **When in doubt, ENABLE**; a button
 * wrongly enabled costs one refused request, a button wrongly disabled is a lie
 * with nobody watching.
 *
 * ⚠️ **The write is a WHOLE-SET REPLACE.** `bands: []` clears the ladder, and
 * the key is required, so an absent body can never read as "clear". Every save
 * sends the entire ladder; clearing it is confirmed rather than blocked.
 *
 * ## Why the bands are sent in DRAFT ORDER and never sorted
 *
 * The overlap refusal arrives at **`bands.<i>.minQuantity`**, pointing at the
 * SECOND of the two ranges — the entry a person would move. That index is a
 * position in the array we sent. Sorting on the way out would leave `i` naming a
 * different row than the one on screen, so the message would mark an innocent
 * band and the real one would look fine. {@link buildBands} therefore preserves
 * order exactly, and {@link routeBandErrors} maps `i` straight to the rendered
 * row.
 *
 * ## Contract
 *
 *   GET /products/{id}/inclusion-presets   any authenticated role
 *   PUT /products/{id}/inclusion-presets   ADMIN_ROLES
 *       body { bands: BandInput[] }        REQUIRED — [] deliberately CLEARS
 *
 * Writes ride on `ability.canUpdate('products')`, which is what every other
 * admin row action on `ProductsPage` gates on — never the role directly and
 * never `/permissions/me`, which grants `advanced_user` a `products.update` the
 * router then refuses with a 403. `requireRole` runs before `validate`, so that
 * 403 carries no `details` and must render as a banner, never as field errors.
 *
 * ## Project mode (2026-10-18, migration 298)
 *
 * With `project` set, the same editor writes the PROJECT's ladder for this one
 * product instead of the product's own:
 *
 *   GET /projects/{id}/inclusion-presets               -> one entry per project product
 *   PUT /projects/{id}/inclusion-presets/{productId}    body { bands }, [] CLEARS
 *
 * A project ladder with any band wins outright for lines on that project — no
 * fallback to the product's ladder, even in a gap. `bands: []` means "uses the
 * product defaults", so clearing here is a REVERT, and the confirmation says so.
 * Writes ride on `ability.canUpdate('projects')`. The pickable set is still the
 * PRODUCT's offer, because the server validates against it. Without `project`
 * nothing below behaves differently from the product-only editor.
 */

/**
 * The minimum this dialog needs about the product — deliberately not the whole
 * `Product`, so a list row and a detail record can both mount it.
 */
export interface ProductInclusionPresetsTarget {
  id: number;
  productName: string;
}

export interface ProductInclusionPresetsModalProps {
  open: boolean;
  /** `null` renders nothing — the caller may leave the prop wired while closed. */
  product: ProductInclusionPresetsTarget | null;
  /**
   * Set => edit this PROJECT's ladder for `product` rather than the product's
   * own. Absent or `null` => the product ladder, exactly as before.
   */
  project?: { id: number; name: string } | null;
  onClose: () => void;
  /** Fired after a successful save, before `onClose`. */
  onSaved: () => void;
}

/** How many skeleton blocks to draw. Enough to read as a ladder, not a page. */
const SKELETON_BANDS = 2;

/** A whole non-negative integer, written out. Anything else is unbuildable. */
const WHOLE_NUMBER = /^\d+$/;

/** One inclusion inside a draft band. The quantity is a STRING — see {@link BandDraft}. */
interface EntryDraft {
  inclusionId: number;
  /** Kept as typed so an emptied box is distinguishable from a real `0`. */
  quantity: string;
}

/**
 * One band being edited.
 *
 * ⚠️ **`min` and `max` are STRINGS and `openEnded` is a separate flag.** An
 * emptied upper bound and a deliberately open-ended band are different facts,
 * and `maxQuantity` has exactly one spelling for the second (`null`) and none at
 * all for the first. Storing a number would make an emptied box read as `0` —
 * a band covering nothing — which is the precise failure the contract warns
 * about, arriving through the form instead of through a `??`.
 */
interface BandDraft {
  /** Stable across edits and independent of the server's ids, which a replace discards. */
  key: string;
  min: string;
  max: string;
  /** `true` => `maxQuantity: null`. NEVER `0`. */
  openEnded: boolean;
  inclusions: EntryDraft[];
}

/** `null` when the text is not a whole number — which is what "unbuildable" means here. */
function parseCount(raw: string): number | null {
  const text = raw.trim();
  return WHOLE_NUMBER.test(text) ? Number(text) : null;
}

let bandKeySeed = 0;
function nextBandKey(): string {
  bandKeySeed += 1;
  return `band-${bandKeySeed}`;
}

/** The wire's ladder as a draft, in the order the server sent it. */
function toDrafts(bands: readonly ProductInclusionPresetBand[]): BandDraft[] {
  return bands.map((band) => ({
    key: nextBandKey(),
    min: String(band.minQuantity),
    /*
     * ⚠️ An open-ended band contributes an EMPTY `max`, never `'0'`. The flag
     * beside it is what carries the meaning; the box is left blank so that
     * unticking "No upper limit" presents an empty field to fill in rather than
     * a `0` that silently means "covers nothing".
     */
    max: band.maxQuantity === null ? '' : String(band.maxQuantity),
    openEnded: band.maxQuantity === null,
    inclusions: band.inclusions.map((entry) => ({
      inclusionId: entry.id,
      quantity: String(entry.quantity),
    })),
  }));
}

/** Whether every number in a band parses, so a payload can be built at all. */
function isBuildable(band: BandDraft): boolean {
  if (parseCount(band.min) === null) return false;
  if (!band.openEnded && parseCount(band.max) === null) return false;
  return band.inclusions.every((entry) => parseCount(entry.quantity) !== null);
}

/**
 * The draft as a `PUT` body.
 *
 * ⚠️ **Order is preserved exactly.** See the header: the server's refusal is
 * indexed, so sorting here would point every band-scoped message at the wrong
 * row. Callers must have checked {@link isBuildable} first — the `?? 0`
 * fallbacks below are unreachable by construction and exist only because
 * `parseCount` is nullable; **they are not a coalesce of a real absence**, and
 * the `openEnded` branch is tested before `max` is read at all so an open-ended
 * band can never take one.
 */
function buildBands(bands: readonly BandDraft[]): ProductInclusionPresetBandInput[] {
  return bands.map((band) => ({
    minQuantity: parseCount(band.min) ?? 0,
    maxQuantity: band.openEnded ? null : (parseCount(band.max) ?? 0),
    inclusions: band.inclusions.map((entry) => ({
      inclusionId: entry.inclusionId,
      quantity: parseCount(entry.quantity) ?? 0,
    })),
  }));
}

/**
 * Which bands overlap one earlier in the ladder — ADVISORY ONLY.
 *
 * Marks the LATER index of each overlapping pair, matching where the server
 * points its own refusal, so the local hint and the eventual 422 land on the
 * same control instead of arguing.
 *
 * ⚠️ **Nothing reads this to disable a control.** See the header.
 */
function overlappingBands(bands: readonly BandDraft[]): ReadonlySet<number> {
  const clash = new Set<number>();
  const ranges = bands.map((band) => {
    const min = parseCount(band.min);
    if (min === null) return null;
    // Open-ended reaches forever. `Infinity` is local arithmetic and never
    // travels — the wire's spelling is `null`.
    const max = band.openEnded ? Infinity : parseCount(band.max);
    return max === null ? null : { min, max };
  });

  for (let later = 0; later < ranges.length; later += 1) {
    const b = ranges[later];
    if (!b) continue;
    for (let earlier = 0; earlier < later; earlier += 1) {
      const a = ranges[earlier];
      if (!a) continue;
      if (a.min <= b.max && b.min <= a.max) {
        clash.add(later);
        break;
      }
    }
  }
  return clash;
}

/** A band's range in the reader's own terms — a VALUE, not a sentence about one. */
function rangeLabel(band: BandDraft): string {
  const min = band.min.trim() || '?';
  if (band.openEnded) return `${min} and above`;
  const max = band.max.trim();
  return max === '' ? `${min} and above?` : `${min}–${max}`;
}

interface RoutedErrors {
  /** Messages bound to no band: form-level refusals, and anything naming `bands` itself. */
  banner: string[];
  /** Band index -> the path BELOW `bands.<i>.`, e.g. `minQuantity`. */
  byBand: ReadonlyMap<number, Record<string, string>>;
}

/**
 * Split a 422 into per-band messages and everything else.
 *
 * The paths this endpoint produces are `bands.<i>.minQuantity`,
 * `bands.<i>.maxQuantity` and `bands.<i>.inclusions.<j>.quantity`. Index `i` is
 * a position in the array we sent, and {@link buildBands} sends draft order, so
 * `i` is the i-th band on screen.
 *
 * ⚠️ **Anything that does not match that shape goes to the BANNER, not to a
 * band.** A path naming something this form does not render binds to no control:
 * routed by its shape it would vanish from the banner, inflate nothing, and
 * highlight nothing — the failure `routeFieldErrors` exists to prevent, here
 * reached through a dotted path instead of a flat key.
 */
function routeBandErrors(error: ApiError): RoutedErrors {
  const banner: string[] = [...error.formMessages];
  const byBand = new Map<number, Record<string, string>>();

  for (const [path, message] of Object.entries(error.fieldErrors)) {
    const match = /^bands\.(\d+)\.(.+)$/.exec(path);
    /*
     * Both groups are tested, not just the match. `noUncheckedIndexedAccess`
     * types them `string | undefined`, and the honest answer to a path this
     * form cannot place is the BANNER — never a band picked by coercing an
     * `undefined` index, which would mark a row the server said nothing about.
     */
    const rest = match?.[2];
    if (!match?.[1] || rest === undefined) {
      // Includes a bare `bands` refusal. Named, so the reader can see which key.
      banner.push(message.includes(path) ? message : `${path}: ${message}`);
      continue;
    }
    const index = Number(match[1]);
    const existing = byBand.get(index) ?? {};
    existing[rest] = message;
    byBand.set(index, existing);
  }

  return { banner, byBand };
}

/**
 * What went wrong on save, at form level.
 *
 * ⚠️ Deliberately not `formMessage ?? message` — both can be true at once (a
 * generic "Validation failed" plus the specific detail), and a coalesce between
 * two things that both need showing is a silent drop.
 */
function describeSaveFailure(
  error: ApiError,
  productName: string,
  banner: string[],
  projectName: string | null = null,
): string {
  if (error.status === 403) {
    return projectName === null
      ? 'Nothing was saved — changing this is an administrator action.'
      : 'Nothing was saved — your account cannot change this project.';
  }
  if (error.status === 404) {
    return projectName === null
      ? `Nothing answered for ${productName}. Close this dialog and reload the list.`
      : `Nothing answered for ${projectName}. Close this dialog and reload the page.`;
  }
  const lines: string[] = [error.message];
  for (const line of banner) {
    if (!lines.includes(line)) lines.push(line);
  }
  return lines.join(' — ');
}

function BandBlock({
  band,
  index,
  total,
  options,
  optionsDisabled,
  labelFor,
  disabled,
  overlapsLocally,
  errors,
  onChange,
  onRemove,
}: {
  band: BandDraft;
  index: number;
  total: number;
  options: { value: string; label: string }[];
  optionsDisabled: boolean;
  labelFor: (inclusionId: number, quantity: string) => string;
  disabled: boolean;
  overlapsLocally: boolean;
  errors: Record<string, string> | undefined;
  onChange: (next: BandDraft) => void;
  onRemove: () => void;
}) {
  /*
   * ⚠️ The SERVER's message wins the slot when both are present. The local
   * check is a guess about a rule the server owns; once the server has spoken
   * about this exact entry, its sentence names BOTH ranges and is the one a
   * person can act on.
   */
  const minError =
    errors?.minQuantity ??
    (overlapsLocally ? 'This range overlaps one above it. Saving will be refused.' : undefined);

  return (
    <div className="grid gap-3 rounded-md border border-border p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="m-0 text-xs font-medium text-fg-secondary">
          Band {index + 1} of {total} · <span className="tabular">{rangeLabel(band)}</span>
        </p>
        {!disabled && (
          <Button variant="ghost" size="sm" onClick={onRemove}>
            Remove band
          </Button>
        )}
      </div>

      {/* One column at 320px; two from `sm`. */}
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="From quantity" error={minError}>
          {(fieldProps) => (
            <Input
              {...fieldProps}
              value={band.min}
              inputMode="numeric"
              autoComplete="off"
              invalid={Boolean(minError)}
              disabled={disabled}
              onChange={(event) => onChange({ ...band, min: event.target.value })}
            />
          )}
        </Field>

        <div className="grid content-start gap-1.5">
          <Field
            label="To quantity"
            error={errors?.maxQuantity}
          >
            {(fieldProps) => (
              <Input
                {...fieldProps}
                value={band.openEnded ? '' : band.max}
                inputMode="numeric"
                autoComplete="off"
                invalid={Boolean(errors?.maxQuantity)}
                /*
                 * Disabled rather than hidden: the reason sits in the tickbox
                 * immediately below it, so the control does not read as one the
                 * reader has not earned.
                 */
                disabled={disabled || band.openEnded}
                onChange={(event) => onChange({ ...band, max: event.target.value })}
              />
            )}
          </Field>
          <Checkbox
            label="No upper limit"
            checked={band.openEnded}
            disabled={disabled}
            /*
             * ⚠️ Unticking restores whatever was typed rather than clearing it,
             * and ticking does not wipe `max`. The flag is the only thing that
             * decides the payload, so keeping the text costs nothing and stops
             * an accidental tick destroying a bound somebody entered.
             */
            onChange={(event) => onChange({ ...band, openEnded: event.target.checked })}
          />
        </div>
      </div>

      <Field label="Inclusions this band presets">
        {(fieldProps) => (
          <MultiCombobox
            {...fieldProps}
            values={band.inclusions.map((entry) => String(entry.inclusionId))}
            onChange={(values) => {
              const wanted = values.map(Number);
              const byId = new Map(band.inclusions.map((entry) => [entry.inclusionId, entry]));
              onChange({
                ...band,
                // Keeps each existing entry's quantity; a newly ticked one
                // starts at 1, which is the contract's own default.
                inclusions: wanted.map(
                  (inclusionId) => byId.get(inclusionId) ?? { inclusionId, quantity: '1' },
                ),
              });
            }}
            options={options}
            placeholder="None"
            searchPlaceholder="Filter inclusions"
            disabled={disabled || optionsDisabled}
            summaryNoun={{ singular: 'inclusion', plural: 'inclusions' }}
            /*
             * A band that presets everything the product offers is an ordinary
             * ladder rung, and ticking five by hand per band is the work this
             * removes. Each newly ticked entry still starts at quantity 1 via
             * `onChange` above — the row toggles membership, never a count.
             */
            selectAll
          />
        )}
      </Field>

      {band.inclusions.length > 0 && (
        <div className="grid gap-2">
          {band.inclusions.map((entry, entryIndex) => {
            const entryError = errors?.[`inclusions.${entryIndex}.quantity`];
            return (
              <div
                key={entry.inclusionId}
                className="flex flex-wrap items-end justify-between gap-2"
              >
                <p className="m-0 min-w-0 flex-1 text-xs text-fg-secondary [overflow-wrap:anywhere]">
                  {labelFor(entry.inclusionId, entry.quantity)}
                </p>
                <div className="w-24 shrink-0">
                  <Field
                    label={`Quantity for inclusion ${entry.inclusionId}`}
                    hideLabel
                    error={entryError}
                  >
                    {(fieldProps) => (
                      <Input
                        {...fieldProps}
                        value={entry.quantity}
                        inputMode="numeric"
                        autoComplete="off"
                        invalid={Boolean(entryError)}
                        disabled={disabled}
                        onChange={(event) => {
                          const next = [...band.inclusions];
                          next[entryIndex] = { ...entry, quantity: event.target.value };
                          onChange({ ...band, inclusions: next });
                        }}
                      />
                    )}
                  </Field>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function ProductInclusionPresetsModal({
  open,
  product,
  project = null,
  onClose,
  onSaved,
}: ProductInclusionPresetsModalProps) {
  const { ability } = useAuth();
  const writeModule = project === null ? 'products' : 'projects';
  const canWrite = ability.canUpdate(writeModule);
  const readOnlyReason = canWrite ? null : denialReason(ability, 'update', writeModule);

  /*
   * Which product this dialog has finished a read for, success or failure.
   * `pending` is DERIVED from it during render rather than being a second flag,
   * so swapping products on a mounted dialog shows loading instantly instead of
   * briefly rendering the previous product's ladder as though it were this one's.
   */
  const [settledFor, setSettledFor] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [bands, setBands] = useState<BandDraft[]>([]);
  /** The ladder as it loaded — the only thing `dirty` compares against. */
  const [loadedKey, setLoadedKey] = useState('');
  /** Every inclusion the read named, by id, so a band can always be labelled. */
  const [known, setKnown] = useState<ReadonlyMap<number, CampaignInclusion>>(new Map());
  /** What the product OFFERS — the pickable set. */
  const [offered, setOffered] = useState<CampaignInclusion[]>([]);
  /**
   * The offer read failed on its own.
   *
   * ⚠️ **This DISABLES the inclusion pickers; it never renders an empty list.**
   * With no options the only reachable edit is removing inclusions from a band,
   * and the save is a whole-set replace — so an unread list would let somebody
   * quietly delete presets they could not see. The ranges stay editable, because
   * those are a different request's data and a different refusal.
   */
  const [offersFailed, setOffersFailed] = useState(false);

  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [bandErrors, setBandErrors] = useState<ReadonlyMap<number, Record<string, string>>>(
    new Map(),
  );
  const [confirmingClear, setConfirmingClear] = useState(false);

  const productId = product?.id ?? null;
  const projectId = project?.id ?? null;
  /** What a read answers for: the product, and the project when there is one. */
  const targetKey = productId === null ? null : `${projectId ?? ''}:${productId}`;
  const pending = targetKey === null || settledFor !== targetKey;

  const load = useCallback((id: number, forProject: number | null, signal: AbortSignal) => {
    const key = `${forProject ?? ''}:${id}`;
    /*
     * The ladder read. In project mode the project endpoint answers for every
     * product the project sells; the entry for THIS product is the ladder, and
     * `null` means the project no longer sells it.
     */
    const ladderRead: Promise<ProductInclusionPresetBand[] | null> =
      forProject === null
        ? api.productInclusionPresets(id, signal).then((response) => response.data)
        : api
            .projectInclusionPresets(forProject, signal)
            .then(
              (response) => response.data.find((entry) => entry.product.id === id)?.bands ?? null,
            );
    return Promise.all([
      ladderRead,
      /*
       * The OFFER read, allowed to fail ON ITS OWN — losing the pickable set
       * must not lose the ladder, whose ranges are perfectly editable without
       * it. Bare, no params: this endpoint's query schema is an empty
       * `.strict()` object, so `?isActive=true` on it is a 422 naming the key.
       */
      api
        .productInclusions(id, signal)
        .then((response) => response.data)
        .catch(() => null),
    ])
      .then(([ladder, offerRows]) => {
        if (signal.aborted) return;
        if (ladder === null) {
          setLoadError('This project no longer sells this product.');
          return;
        }
        setLoadError(null);

        const drafts = toDrafts(ladder);
        setBands(drafts);
        setLoadedKey(ladderKey(drafts));

        /*
         * Every inclusion named ANYWHERE in the read, whether the product still
         * offers it or not. This is the retired-value-injection rule: the write
         * replaces the whole ladder, so a band entry missing from the options
         * would render as a bare id and then be dropped by the next save —
         * deleting a preset nobody chose to delete.
         */
        const seen = new Map<number, CampaignInclusion>();
        for (const band of ladder) {
          for (const entry of band.inclusions) seen.set(entry.id, entry);
        }
        for (const inclusion of offerRows ?? []) seen.set(inclusion.id, inclusion);
        setKnown(seen);

        setOffered(offerRows ?? []);
        setOffersFailed(offerRows === null);
      })
      .catch((error: unknown) => {
        if (signal.aborted) return;
        setLoadError(
          error instanceof ApiError
            ? `Couldn’t load the presets. ${error.message}`
            : 'Could not reach the server.',
        );
      })
      .finally(() => {
        if (signal.aborted) return;
        setFormError(null);
        setBandErrors(new Map());
        setConfirmingClear(false);
        setSettledFor(key);
      });
  }, []);

  useEffect(() => {
    if (!open || productId === null) return;
    const controller = new AbortController();
    void load(productId, projectId, controller.signal);
    return () => controller.abort();
  }, [open, productId, projectId, load]);

  const draftKey = useMemo(() => ladderKey(bands), [bands]);
  const dirty = draftKey !== loadedKey;

  const overlaps = useMemo(() => overlappingBands(bands), [bands]);

  /** Bands whose numbers do not parse — the only thing that can stop a save. */
  const unbuildable = useMemo(
    () => bands.reduce((count, band) => (isBuildable(band) ? count : count + 1), 0),
    [bands],
  );

  /**
   * The pickable set, plus any inclusion a band already names that the product
   * no longer offers — labelled, never dropped. See the read for why.
   */
  const options = useMemo(() => {
    const offeredIds = new Set(offered.map((inclusion) => inclusion.id));
    const rows = offered.map((inclusion) => ({
      value: String(inclusion.id),
      label: inclusion.name,
    }));
    const extra = new Set<number>();
    for (const band of bands) {
      for (const entry of band.inclusions) {
        if (!offeredIds.has(entry.inclusionId)) extra.add(entry.inclusionId);
      }
    }
    for (const id of extra) {
      const row = known.get(id);
      rows.push({
        value: String(id),
        label: `${row?.name ?? `Inclusion ${id}`} (not offered on this product)`,
      });
    }
    return rows;
  }, [offered, bands, known]);

  /**
   * One band entry, in the inclusion wording rule's own words.
   *
   * ⚠️ `renderInclusion` from `@/lib/inclusions` is the ONE copy of the
   * singular/plural rule, and nothing here may grow a second: the same text
   * prints on a quote a customer is invoiced against, and a client-side
   * pluraliser's first act is `1 campuses`.
   */
  const labelFor = useCallback(
    (inclusionId: number, quantity: string) => {
      const row = known.get(inclusionId);
      if (!row) return `Inclusion ${inclusionId}`;
      const count = parseCount(quantity);
      // An unparsed box shows the bare name rather than a wording built from a
      // number nobody typed.
      return count === null ? row.name.trim() : renderInclusion(row, count);
    },
    [known],
  );

  /*
   * The ONE writer of the draft, so a stale refusal can never outlive the edit
   * that answered it: a message marking `band 2` after somebody has removed
   * band 1 would point at a row the server never refused. Cleared on every
   * edit rather than per field, because the indexes shift.
   */
  function updateBands(next: BandDraft[]) {
    if (bandErrors.size > 0) setBandErrors(new Map());
    if (formError !== null) setFormError(null);
    setBands(next);
  }

  function addBand() {
    /*
     * Seeds the start from the last band's end — a default on ONE new row, not
     * a repair of the ladder. A gap is legal and is never filled in for anybody;
     * an open-ended or unparsed last band seeds nothing, because there is no
     * honest number to offer.
     */
    const last = bands[bands.length - 1];
    const previousEnd = last && !last.openEnded ? parseCount(last.max) : null;
    updateBands([
      ...bands,
      {
        key: nextBandKey(),
        min: previousEnd === null ? '' : String(previousEnd + 1),
        max: '',
        openEnded: false,
        inclusions: [],
      },
    ]);
  }

  async function put(): Promise<void> {
    if (productId === null || product === null) return;

    setSaving(true);
    setFormError(null);
    setBandErrors(new Map());
    try {
      /*
       * ⚠️ `bands` is ALWAYS sent, including when it is empty. The key is
       * required — an absent one is a 422 — and `[]` is the explicit "preset
       * nothing at any quantity". Two opposite meanings for one falsy value, so
       * this must never become a conditional spread or an `if (bands.length)`
       * guard: the intent dropped would be the one just confirmed.
       *
       * And it is a FULL REPLACE in DRAFT ORDER — see `buildBands`.
       */
      if (project === null) {
        await api.setProductInclusionPresets(productId, buildBands(bands));
      } else {
        await api.setProjectInclusionPresets(project.id, productId, buildBands(bands));
      }
      onSaved();
      onClose();
    } catch (error) {
      if (error instanceof ApiError) {
        const routed = routeBandErrors(error);
        setBandErrors(routed.byBand);
        setFormError(
          describeSaveFailure(error, product.productName, routed.banner, project?.name ?? null),
        );
      } else {
        setFormError('Could not reach the server. Nothing was saved — check your connection.');
      }
    } finally {
      setSaving(false);
      setConfirmingClear(false);
    }
  }

  function handleSave() {
    if (saving || !dirty || !canWrite || unbuildable > 0) return;
    // Emptying the ladder is legitimate, so it is confirmed rather than blocked.
    if (bands.length === 0 && loadedKey !== '') {
      setConfirmingClear(true);
      return;
    }
    void put();
  }

  if (!open || product === null) return null;

  const productName = product.productName;
  /*
   * ⚠️ Named beside the button, never a bare `disabled`. A disabled control is a
   * claim, and an unexplained one makes "nothing to save", "you may not do this"
   * and "a number does not parse" look identical.
   */
  const disabledReason = !dirty
    ? null
    : unbuildable > 0
      ? `${unbuildable} ${unbuildable === 1 ? 'band needs' : 'bands need'} a whole number in every box.`
      : null;

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title={
          project === null
            ? 'Campaign inclusion presets'
            : `Campaign inclusion presets · ${project.name} · ${productName}`
        }
        size="lg"
        // A half-built ladder must not vanish on a clipped backdrop click.
        dismissOnBackdrop={false}
        description={readOnlyReason ?? undefined}
        footer={
          canWrite ? (
            <>
              <Button variant="secondary" onClick={onClose} disabled={saving}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSave}
                loading={saving}
                /*
                 * ⚠️ An OVERLAP is deliberately NOT in this list. The server owns
                 * that rule; blocking here would make a local guess the only
                 * guard and would refuse a legal ladder the first time the two
                 * disagreed. See the file header.
                 */
                disabled={pending || Boolean(loadError) || !dirty || unbuildable > 0}
              >
                Save presets
              </Button>
            </>
          ) : (
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
          )
        }
      >
        <div className="grid gap-4">
          {formError && (
            <Notice tone="danger" role="alert">
              <span className="[overflow-wrap:anywhere]">
                {formError}
                {bandErrors.size > 0 && (
                  <>
                    {' '}
                    Marked on{' '}
                    {[...bandErrors.keys()]
                      .sort((a, b) => a - b)
                      .map((index) => `band ${index + 1}`)
                      .join(', ')}
                    .
                  </>
                )}
              </span>
            </Notice>
          )}

          {!canWrite && readOnlyReason && (
            <Notice tone="neutral">
              {project === null
                ? `${readOnlyReason} Changing it is an administrator action.`
                : readOnlyReason}
            </Notice>
          )}

          {/* 1. LOADING — the shape of what is coming, not a spinner. */}
          {pending && (
            <div className="grid gap-3" aria-hidden="true">
              {Array.from({ length: SKELETON_BANDS }, (_, index) => (
                <div key={index} className="grid gap-3 rounded-md border border-border p-3">
                  <Skeleton className="h-3.5 w-32 max-w-full" />
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Skeleton className="h-9 w-full" />
                    <Skeleton className="h-9 w-full" />
                  </div>
                  <Skeleton className="h-9 w-full" />
                </div>
              ))}
            </div>
          )}

          {/* 2. ERROR — names what failed, and offers no half-loaded ladder. */}
          {!pending && loadError && (
            <Notice tone="danger" role="alert">
              <span className="[overflow-wrap:anywhere]">
                {loadError} Nothing has changed — close this and try again.
              </span>
            </Notice>
          )}

          {!pending && !loadError && (
            <>
              {offersFailed && (
                <Notice tone="warning" role="alert">
                  The inclusions this product offers could not be read, so they cannot be changed
                  here. The quantity ranges are still editable.
                </Notice>
              )}

              {/* 3. EMPTY — a real state, and the whole point of the Add control. */}
              {bands.length === 0 ? (
                project === null ? (
                  <EmptyState
                    title="No quantity bands"
                    description="A line naming this product starts with nothing ticked, at every quantity."
                  />
                ) : (
                  <EmptyState
                    title="Uses product defaults"
                    description={`A line on ${project.name} naming ${productName} starts with the product’s own bands.`}
                  />
                )
              ) : (
                <div className="grid gap-3">
                  {bands.map((band, index) => (
                    <BandBlock
                      key={band.key}
                      band={band}
                      index={index}
                      total={bands.length}
                      options={options}
                      optionsDisabled={offersFailed}
                      labelFor={labelFor}
                      disabled={!canWrite || saving}
                      overlapsLocally={overlaps.has(index)}
                      errors={bandErrors.get(index)}
                      onChange={(next) =>
                        updateBands(bands.map((row) => (row.key === band.key ? next : row)))
                      }
                      onRemove={() =>
                        updateBands(bands.filter((row) => row.key !== band.key))
                      }
                    />
                  ))}
                </div>
              )}

              {canWrite && (
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Button variant="secondary" size="sm" onClick={addBand} disabled={saving}>
                    Add a band
                  </Button>
                  {disabledReason && (
                    <p className="m-0 text-xs text-fg-muted [overflow-wrap:anywhere]">
                      {disabledReason}
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </Modal>

      <ConfirmDialog
        open={confirmingClear}
        onClose={() => setConfirmingClear(false)}
        onConfirm={() => void put()}
        title={
          project === null
            ? 'Preset nothing on this product?'
            : `Revert ${productName} on ${project.name} to product defaults?`
        }
        confirmLabel={project === null ? 'Save with no bands' : 'Revert to product defaults'}
        loading={saving}
        description={
          project === null ? (
            <>
              <p className="m-0">
                Removes every quantity band from {productName}. A line naming it will start with no
                inclusions ticked, at any quantity.
              </p>
              <p className="m-0 mt-2">
                Existing quote and invoice lines keep theirs. Reversible — add the bands back.
              </p>
            </>
          ) : (
            <>
              <p className="m-0">
                Removes every quantity band for {productName} on {project.name}. New lines on this
                project revert to {productName}’s own defaults.
              </p>
              <p className="m-0 mt-2">
                Existing deal, quote and invoice lines keep theirs. Reversible — add the bands back.
              </p>
            </>
          )
        }
      />
    </>
  );
}

/**
 * The whole ladder as one comparable string — what `dirty` is measured against.
 *
 * ⚠️ **Includes the ORDER and the open-ended flag.** Order decides which band a
 * server message marks, and `openEnded` decides `null` versus a number, so
 * neither may be normalised away: two ladders that differ only in those are two
 * different writes.
 */
function ladderKey(bands: readonly BandDraft[]): string {
  return bands
    .map(
      (band) =>
        `${band.min.trim()}-${band.openEnded ? '*' : band.max.trim()}:${band.inclusions
          .map((entry) => `${entry.inclusionId}x${entry.quantity.trim()}`)
          .join(',')}`,
    )
    .join('|');
}
